import { ExpenseGroup } from "../../types/daily-expense.type";
import {
  MonthlyExpenseBranchInput,
  MonthlyExpenseBranchRow,
  MonthlyExpenseGroup,
  MonthlyExpenseMonth,
} from "../../types/monthly-expense.type";
import {
  EstimateFigures,
  GroupedAmount,
  MonthTotals,
  estimateFiguresOf,
  perRouteDay,
  roundUpEstimate,
  sumMoney,
  totalsOf,
} from "./monthlyExpenseMath";

/**
 * S4's editable model and its rules: what head office typed over the server's month, the cell
 * commits (parse + validate), the adjustment tools (CEIL, คืนค่าระบบ, คัดลอกยอดเดือนก่อน, extras),
 * the live figures, and the บันทึกร่าง payload of the branches that changed. Pure, so the screen
 * and the tests read one set of rules.
 */

export type EstimateSide = "SALES" | "MANAGER";
export const ESTIMATE_SIDES: EstimateSide[] = ["SALES", "MANAGER"];

export interface ExtraDraft {
  /** The saved extra's uuid, or a local key for one added since the last save. */
  key: string;
  uuid: string | null;
  group: MonthlyExpenseGroup;
  description: string;
  amount: number;
  createdByName: string | null;
  createdAt: string | null;
}

export interface BranchDraft {
  /** dailyCellKeyOf(category, group) → the amount used. */
  daily: Record<string, number>;
  /** category code → amount. */
  items: Record<string, number>;
  extras: ExtraDraft[];
  /** Head office's M+1 estimate; null = follow the computed value. */
  estimate: Record<EstimateSide, number | null>;
}

export type CloseCellTarget =
  | { kind: "DAILY"; branchCode: string; categoryCode: string; group: ExpenseGroup }
  | { kind: "ITEM"; branchCode: string; categoryCode: string }
  | { kind: "ESTIMATE"; branchCode: string; side: EstimateSide };

/** A cell whose typing was refused: the text stays in the cell with the reason. */
export interface CellError {
  text: string;
  message: string;
}

export interface CloseDraft {
  /** By branch code. */
  branches: Record<string, BranchDraft>;
  /** By targetKeyOf. */
  errors: Record<string, CellError>;
}

export const dailyCellKeyOf = (categoryCode: string, group: ExpenseGroup) =>
  `${categoryCode}|${group}`;

export const targetKeyOf = (target: CloseCellTarget): string => {
  if (target.kind === "DAILY") {
    return `${target.branchCode}|DAILY|${dailyCellKeyOf(target.categoryCode, target.group)}`;
  }
  if (target.kind === "ITEM") return `${target.branchCode}|ITEM|${target.categoryCode}`;
  return `${target.branchCode}|ESTIMATE|${target.side}`;
};

// ---------- reading the server's month ----------

/** The read rule of the contract: an estimate equal to the computed value is not an adjustment. */
const adjustedOf = (value: number | null, computed: number | null) =>
  value !== null && value !== computed ? value : null;

export const branchDraftOf = (row: MonthlyExpenseBranchRow): BranchDraft => ({
  daily: Object.fromEntries(
    row.daily.map((cell) => [dailyCellKeyOf(cell.category_code, cell.expense_group), cell.amount]),
  ),
  items: Object.fromEntries(row.items.map((item) => [item.category_code, item.amount])),
  extras: row.extras.map((extra) => ({
    key: extra.uuid,
    uuid: extra.uuid,
    group: extra.expense_group,
    description: extra.description ?? "",
    amount: extra.amount,
    createdByName: extra.created_by_name,
    createdAt: extra.created_at,
  })),
  estimate: {
    SALES: adjustedOf(row.estimate.sales_per_route_day, row.estimate.computed_sales_per_route_day),
    MANAGER: adjustedOf(
      row.estimate.manager_per_route_day,
      row.estimate.computed_manager_per_route_day,
    ),
  },
});

export const closeDraftOf = (month: MonthlyExpenseMonth): CloseDraft => ({
  branches: Object.fromEntries(month.rows.map((row) => [row.branch_code, branchDraftOf(row)])),
  errors: {},
});

// ---------- live figures ----------

export interface BranchFigures {
  totals: MonthTotals;
  /** The รวม column of each group (จากรายวัน = both of its parts). */
  groupTotals: Record<"DAILY" | MonthlyExpenseGroup, number>;
  estimate: Record<EstimateSide, EstimateFigures>;
}

export const routeDaysOf = (row: MonthlyExpenseBranchRow) => row.sale_days * row.route_count;

export const extrasTotalOf = (draft: BranchDraft, group: MonthlyExpenseGroup) =>
  sumMoney(draft.extras.filter((extra) => extra.group === group).map((extra) => extra.amount));

/**
 * While the month is open the computed estimate follows what is typed; once closed it is the
 * frozen figure the server answers.
 */
export const branchFiguresOf = (
  row: MonthlyExpenseBranchRow,
  draft: BranchDraft,
  isLive: boolean,
): BranchFigures => {
  const daily: GroupedAmount[] = row.daily.map((cell) => ({
    group: cell.expense_group,
    amount: draft.daily[dailyCellKeyOf(cell.category_code, cell.expense_group)] ?? 0,
  }));
  const monthly: GroupedAmount[] = [
    ...row.items.map((item) => ({ group: item.expense_group, amount: draft.items[item.category_code] ?? 0 })),
    ...draft.extras.map((extra) => ({ group: extra.group, amount: extra.amount })),
  ];
  const totals = totalsOf(daily, monthly);
  const computed: Record<EstimateSide, number | null> = isLive
    ? {
        SALES: perRouteDay(totals.salesTotal, row.sale_days, row.route_count),
        MANAGER: perRouteDay(totals.managerTotal, row.sale_days, row.route_count),
      }
    : {
        SALES: row.estimate.computed_sales_per_route_day,
        MANAGER: row.estimate.computed_manager_per_route_day,
      };
  const routeDays = routeDaysOf(row);
  return {
    totals,
    groupTotals: {
      DAILY: sumMoney([totals.dailySales, totals.dailyAdmin]),
      SALES: totals.sales,
      SALARY: totals.salary,
      ADMIN: totals.admin,
    },
    estimate: {
      SALES: estimateFiguresOf(computed.SALES, draft.estimate.SALES, routeDays),
      MANAGER: estimateFiguresOf(computed.MANAGER, draft.estimate.MANAGER, routeDays),
    },
  };
};

// ---------- typing ----------

const THOUSANDS_SEPARATOR = /,/g;
const NUMBER_PATTERN = /^-?\d+(\.\d+)?$/;
const MONEY_PATTERN = /^\d+(\.\d{1,2})?$/;
export const NOT_A_NUMBER_TEXT = "กรุณาใส่ตัวเลข";
export const NEGATIVE_TEXT = "ตัวเลข ≥ 0";
export const TOO_MANY_DECIMALS_TEXT = "ทศนิยมไม่เกิน 2 ตำแหน่ง";

/** Blank = null; else a non-negative amount with at most 2 decimals (commas allowed). */
export const parseMoneyText = (text: string): { amount: number | null } | { message: string } => {
  const cleaned = text.replace(THOUSANDS_SEPARATOR, "").trim();
  if (cleaned === "") return { amount: null };
  if (!NUMBER_PATTERN.test(cleaned)) return { message: NOT_A_NUMBER_TEXT };
  if (Number(cleaned) < 0) return { message: NEGATIVE_TEXT };
  if (!MONEY_PATTERN.test(cleaned)) return { message: TOO_MANY_DECIMALS_TEXT };
  return { amount: Number(cleaned) };
};

const withBranch = (draft: CloseDraft, branchCode: string, change: (branch: BranchDraft) => BranchDraft) => ({
  ...draft,
  branches: { ...draft.branches, [branchCode]: change(draft.branches[branchCode]) },
});

const withoutErrors = (errors: Record<string, CellError>, isDropped: (key: string) => boolean) =>
  Object.fromEntries(Object.entries(errors).filter(([key]) => !isDropped(key)));

/**
 * A cell left with new text. An amount cell blanked counts 0; an estimate cell blanked goes back
 * to the system value. Refused text stays in the cell, flagged, and changes nothing.
 */
export const commitCell = (draft: CloseDraft, target: CloseCellTarget, text: string): CloseDraft => {
  const parsed = parseMoneyText(text);
  const errorKey = targetKeyOf(target);
  if ("message" in parsed) {
    return { ...draft, errors: { ...draft.errors, [errorKey]: { text, message: parsed.message } } };
  }
  const cleared = { ...draft, errors: withoutErrors(draft.errors, (key) => key === errorKey) };
  return withBranch(cleared, target.branchCode, (branch) => {
    if (target.kind === "DAILY") {
      const cellKey = dailyCellKeyOf(target.categoryCode, target.group);
      return { ...branch, daily: { ...branch.daily, [cellKey]: parsed.amount ?? 0 } };
    }
    if (target.kind === "ITEM") {
      return { ...branch, items: { ...branch.items, [target.categoryCode]: parsed.amount ?? 0 } };
    }
    return { ...branch, estimate: { ...branch.estimate, [target.side]: parsed.amount } };
  });
};

const isEstimateErrorKey = (key: string) => key.includes("|ESTIMATE|");

/** ปัดขึ้นเป็นจำนวนเต็ม over the whole table; a branch with no figure (no route ran) is skipped. */
export const roundUpAll = (
  draft: CloseDraft,
  rows: MonthlyExpenseBranchRow[],
  isLive: boolean,
): { draft: CloseDraft; changedCount: number } => {
  let changedCount = 0;
  const branches = { ...draft.branches };
  rows.forEach((row) => {
    const branch = draft.branches[row.branch_code];
    const figures = branchFiguresOf(row, branch, isLive);
    const estimate = { ...branch.estimate };
    ESTIMATE_SIDES.forEach((side) => {
      const { effective } = figures.estimate[side];
      if (effective === null) return;
      const roundedUp = roundUpEstimate(effective);
      if (roundedUp !== effective) changedCount += 1;
      estimate[side] = roundedUp;
    });
    branches[row.branch_code] = { ...branch, estimate };
  });
  return { draft: { branches, errors: withoutErrors(draft.errors, isEstimateErrorKey) }, changedCount };
};

/** คืนค่าระบบ of one Sales / Manager cell. */
export const revertEstimate = (draft: CloseDraft, branchCode: string, side: EstimateSide): CloseDraft => {
  const errorKey = targetKeyOf({ kind: "ESTIMATE", branchCode, side });
  return withBranch(
    { ...draft, errors: withoutErrors(draft.errors, (key) => key === errorKey) },
    branchCode,
    (branch) => ({ ...branch, estimate: { ...branch.estimate, [side]: null } }),
  );
};

/** คืนค่าระบบทั้งหมด: every estimate follows the computed value again (extras stay). */
export const revertAllEstimates = (draft: CloseDraft): CloseDraft => ({
  branches: Object.fromEntries(
    Object.entries(draft.branches).map(([code, branch]) => [
      code,
      { ...branch, estimate: { SALES: null, MANAGER: null } },
    ]),
  ),
  errors: withoutErrors(draft.errors, isEstimateErrorKey),
});

const isItemErrorKey = (key: string) => key.includes("|ITEM|");

/**
 * คัดลอกยอดเดือนก่อน: the 16 items of every branch become last month's (an item not keyed then is
 * 0). A branch with nothing keyed last month keeps what was typed.
 */
export const copyPreviousItems = (
  draft: CloseDraft,
  rows: MonthlyExpenseBranchRow[],
): { draft: CloseDraft; copiedCount: number } => {
  const copied = rows.filter((row) => row.previous_items.length > 0);
  const branches = { ...draft.branches };
  copied.forEach((row) => {
    const previous = new Map(row.previous_items.map((item) => [item.category_code, item.amount]));
    branches[row.branch_code] = {
      ...draft.branches[row.branch_code],
      items: Object.fromEntries(
        row.items.map((item) => [item.category_code, previous.get(item.category_code) ?? 0]),
      ),
    };
  });
  const copiedCodes = new Set(copied.map((row) => row.branch_code));
  const errors = withoutErrors(
    draft.errors,
    (key) => isItemErrorKey(key) && copiedCodes.has(key.split("|")[0]),
  );
  return { draft: { branches, errors }, copiedCount: copied.length };
};

export interface ExtraInput {
  branchCodes: string[];
  group: MonthlyExpenseGroup;
  description: string;
  amount: number;
}

/** + เพิ่มค่าใช้จ่าย: the same extra on each branch picked. `sequence` keeps the local keys unique. */
export const addExtras = (draft: CloseDraft, input: ExtraInput, sequence: number): CloseDraft =>
  input.branchCodes.reduce(
    (current, branchCode) =>
      withBranch(current, branchCode, (branch) => ({
        ...branch,
        extras: [
          ...branch.extras,
          {
            key: `new-${sequence}-${branchCode}`,
            uuid: null,
            group: input.group,
            description: input.description.trim(),
            amount: input.amount,
            createdByName: null,
            createdAt: null,
          },
        ],
      })),
    draft,
  );

export const removeExtra = (draft: CloseDraft, branchCode: string, key: string): CloseDraft =>
  withBranch(draft, branchCode, (branch) => ({
    ...branch,
    extras: branch.extras.filter((extra) => extra.key !== key),
  }));

// ---------- saving ----------

/**
 * One branch as บันทึกร่าง sends it: only the daily cells typed over (the rest follow the approved
 * sum), the items keyed, every extra (a saved one with its uuid), and an estimate only where it
 * differs from the computed value.
 */
export const branchInputOf = (
  row: MonthlyExpenseBranchRow,
  draft: BranchDraft,
  isLive: boolean,
): MonthlyExpenseBranchInput => {
  const { estimate } = branchFiguresOf(row, draft, isLive);
  const valueOf = (side: EstimateSide) => (estimate[side].isAdjusted ? draft.estimate[side] : null);
  return {
    branch_code: row.branch_code,
    daily_overrides: row.daily.flatMap((cell) => {
      const amount = draft.daily[dailyCellKeyOf(cell.category_code, cell.expense_group)] ?? 0;
      return amount === cell.system_amount
        ? []
        : [{ category_code: cell.category_code, expense_group: cell.expense_group, amount }];
    }),
    items: row.items.flatMap((item) => {
      const amount = draft.items[item.category_code] ?? 0;
      return amount === 0 ? [] : [{ category_code: item.category_code, amount }];
    }),
    extras: draft.extras.map((extra) => ({
      ...(extra.uuid ? { uuid: extra.uuid } : {}),
      expense_group: extra.group,
      description: extra.description,
      amount: extra.amount,
    })),
    estimate: { sales_per_route_day: valueOf("SALES"), manager_per_route_day: valueOf("MANAGER") },
  };
};

/** The branches whose draft differs from what the server holds — what บันทึกร่าง sends. */
export const changedBranchInputsOf = (
  rows: MonthlyExpenseBranchRow[],
  draft: CloseDraft,
  isLive: boolean,
): MonthlyExpenseBranchInput[] =>
  rows.flatMap((row) => {
    const current = branchInputOf(row, draft.branches[row.branch_code], isLive);
    const saved = branchInputOf(row, branchDraftOf(row), isLive);
    return JSON.stringify(current) === JSON.stringify(saved) ? [] : [current];
  });

// ---------- the dialogs ----------

export const EXTRA_BRANCH_REQUIRED_TEXT = "กรุณาเลือกอย่างน้อย 1 สาขา";
export const EXTRA_DESCRIPTION_REQUIRED_TEXT = "กรุณาใส่คำอธิบาย";
export const EXTRA_AMOUNT_TEXT = "ตัวเลขมากกว่า 0";

export interface ExtraForm {
  branchCodes: string[];
  group: MonthlyExpenseGroup;
  description: string;
  amountText: string;
}

export type ExtraFormErrors = Partial<Record<"branchCodes" | "description" | "amountText", string>>;

/** The dialog's rules: a branch, a description, and an amount above 0 with at most 2 decimals. */
export const extraFormErrorsOf = (form: ExtraForm): ExtraFormErrors => {
  const errors: ExtraFormErrors = {};
  if (form.branchCodes.length === 0) errors.branchCodes = EXTRA_BRANCH_REQUIRED_TEXT;
  if (form.description.trim() === "") errors.description = EXTRA_DESCRIPTION_REQUIRED_TEXT;
  const parsed = parseMoneyText(form.amountText);
  if ("message" in parsed) {
    errors.amountText = parsed.message === TOO_MANY_DECIMALS_TEXT ? parsed.message : EXTRA_AMOUNT_TEXT;
  } else if (parsed.amount === null || parsed.amount <= 0) {
    errors.amountText = EXTRA_AMOUNT_TEXT;
  }
  return errors;
};

/** What the ส่งและปิดเดือน dialog and the summary cards show. */
export interface CloseSummary {
  branchCount: number;
  routeCount: number;
  monthTotal: number;
  extraCount: number;
  extraTotal: number;
  adjustedCount: number;
  monthlyImpact: number;
  /** Branches with daily documents the close will not count, and how many days. */
  unapprovedBranchCount: number;
  unapprovedDayCount: number;
}

export const closeSummaryOf = (
  rows: MonthlyExpenseBranchRow[],
  draft: CloseDraft,
  figures: BranchFigures[],
): CloseSummary => {
  const adjusted = figures.flatMap((figure) =>
    ESTIMATE_SIDES.map((side) => figure.estimate[side]).filter((estimate) => estimate.isAdjusted),
  );
  const extras = rows.flatMap((row) => draft.branches[row.branch_code].extras);
  const unapprovedDays = rows.map(
    ({ warnings }) => warnings.not_submitted_count + warnings.pending_count + warnings.rejected_count,
  );
  return {
    branchCount: rows.length,
    routeCount: rows.reduce((sum, row) => sum + row.route_count, 0),
    monthTotal: sumMoney(figures.map((figure) => figure.totals.total)),
    extraCount: extras.length,
    extraTotal: sumMoney(extras.map((extra) => extra.amount)),
    adjustedCount: adjusted.length,
    monthlyImpact: sumMoney(adjusted.map((estimate) => estimate.monthlyImpact)),
    unapprovedBranchCount: unapprovedDays.filter((days) => days > 0).length,
    unapprovedDayCount: unapprovedDays.reduce((sum, days) => sum + days, 0),
  };
};
